import {
    useState, useContext, useEffect
} from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose
} from 'shared/components/ui/dialog';
import {
    ScrollArea
} from 'shared/components/ui/scroll-area';
import {
    Card, CardHeader, CardTitle, CardDescription, CardContent
} from 'shared/components/ui/card';
import {
    Button
} from 'shared/components/ui/button';
import {
    User, Clock, Bot, Settings2, InfoIcon
} from 'lucide-react';
import {
    Label
} from 'shared/components/ui/label';
import {
    FilterBar, SearchBar
} from 'builders/builderInternal/components/Instructions/Filters';
import {
    AiLabContext
} from 'builders/builderInternal/context/AiLabContext';
import {
    Badge
} from 'shared/components/ui/badge';
import {
    Context
} from 'shared/context/GlobalState';
import labHelper from 'builders/builderInternal/classes/AILabHelper';
import {
    OrganizationContext
} from 'shared/context/OrganizationContext';
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from 'shared/components/ui/tooltip';

function DetailsCard({
    instructionDetails, handleInstructionLoad, selectedInstruction
}) {
    return (
        <div className="md:w-1/3 my-4 min-w-[300px] flex flex-col gap-3">
            <Card className="h-[30rem] overflow-y-auto">
                <div className="flex justify-end gap-2 p-4">
                    <DialogClose asChild>
                        <Button type="button" variant="secondary">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        onClick={handleInstructionLoad}
                        disabled={!selectedInstruction}
                        variant="default"
                    >
                        Load Instruction
                    </Button>
                </div>
                <CardHeader>
                    <div className="flex items-start justify-between">
                        <div className="space-y-1">
                            <CardTitle className="text-xl whitespace-pre-wrap break-all">{instructionDetails?.unique_name}</CardTitle>
                            <CardDescription className="text-xs">Instruction</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-2.5">
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs">
                            <User className="h-4 w-4" />
                            <Label className="font-medium text-xs">Created By:</Label>
                            <span className="text-muted-foreground">{instructionDetails?.user_id || '-'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                            <Clock className="h-4 w-4" />
                            <Label className="font-medium text-xs">Modified By:</Label>
                            <span className="text-muted-foreground">{instructionDetails?.modified_by || '-'}</span>
                        </div>
                    </div>
                    <hr />
                    <div className="space-y-2">
                        <div className="">
                            <Label className="text-xs font-medium">Description</Label>
                            <p className="text-xs text-muted-foreground">{instructionDetails?.modelDescription || '-'}</p>
                        </div>
                        <div className="space-y">
                            <Label className="text-xs font-medium">Prompt</Label>
                            <p className="text-xs text-muted-foreground whitespace-pre-wrap break-all">{instructionDetails?.prompt || '-'}</p>
                        </div>
                    </div>
                    <hr />
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Bot className="h-4 w-4" />
                            <Label className="text-xs font-medium">Model ID</Label>
                        </div>
                        <code className="rounded bg-muted px-2 py-1 text-xs whitespace-pre-wrap break-all">{instructionDetails?.modelId}</code>
                    </div>
                    <hr />
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Settings2 className="h-4 w-4" />
                            <Label className="text-xs font-medium">Parameters</Label>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                            {instructionDetails?.parameters && Object.entries(instructionDetails?.parameters)
                                .filter(([key]) => key !== 'repetition_penalty' && key !== 'stop_sequence' && key !== 'n' && key !== 'stop' && key !== 'stream' && key !== 'seed' && key !== 'response_format')
                                .map(([key, value]) => (
                                    <div className="" key={key}>
                                        <Label className="text-xs font-medium">{labHelper.formatToTitleCase(key)}</Label>
                                        <p className="text-sm text-muted-foreground">{value}</p>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export default function CommonInstructionSelectionModal({
    setForm, openModal, setOpenModal, onLoadInstruction = null
}) {
    const [selectType, setSelectType] = useState('All');
    const [modelTypes, setModelTypes] = useState(['All']);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedInstruction, setSelectedInstruction] = useState(null);
    const [instructionDetails, setInstructionDetails] = useState(null);

    const {
        dispatch, instructions, fetchInstructions, isLoadingInstructions
    } = useContext(AiLabContext);
    const {
        addNewNotifcation
    } = useContext(Context);
    const {
        selectedOrganization
    } = useContext(OrganizationContext);

    useEffect(() => {
        const uniqueModelTypes = selectedOrganization?.providers || [];
        setModelTypes(prev => [...prev, ...uniqueModelTypes]);
    }, []);

    const reloadInstructions = async () => {
        try {
            dispatch({
                type: 'SET_INSTRUCTIONS',
                field: 'instructions',
                value: []
            });
            await fetchInstructions();
        } catch (error) {
            console.error(error);
            addNewNotifcation('Error fetching instructions. Please try again.', 'danger');
        }
    };

    const handleInstructionSelect = (instruction) => {
        if (selectedInstruction === instruction?.unique_name) {
            setSelectedInstruction(null);
            setInstructionDetails(null);
        } else {
            //
            setSelectedInstruction(instruction?.unique_name);
            setInstructionDetails(instruction);
        }
    };

    const handleInstructionLoad = () => {
        const instructionToLoad = instructions.find((inst) => inst?.unique_name === selectedInstruction);

        setForm('unique_name', instructionToLoad?.unique_name || '');
        setForm('description', instructionToLoad?.modelDescription || '');
        setForm('model_type', instructionToLoad?.model_type || '');
        setForm('selectedModel', instructionToLoad?.modelId || '');
        setForm('instruction', instructionToLoad?.prompt || '');
        setForm('region', instructionToLoad?.region || '');

        if (instructionToLoad?.model_type === 'IBM') {
            setForm('watsonParameters', instructionToLoad?.parameters);
        } else if (instructionToLoad?.model_type === 'Groq') {
            setForm('groqParameters', instructionToLoad?.parameters);
        } else if (instructionToLoad?.model_type === 'Cerebras') {
            setForm('cerebrasParameters', instructionToLoad?.parameters);
        } else if (instructionToLoad?.model_type === 'SambaNova') {
            setForm('sambaNovaParameters', instructionToLoad?.parameters);
        } else if (instructionToLoad?.model_type === 'OnPrem') {
            setForm('onPremParameters', instructionToLoad?.parameters);
        } else if (instructionToLoad?.model_type === 'Gemini') {
            setForm('geminiParameters', instructionToLoad?.parameters);
        } else if (instructionToLoad?.model_type === 'Mistral') {
            setForm('mistralParameters', instructionToLoad?.parameters);
        } else if (instructionToLoad?.model_type === 'Novita') {
            setForm('novitaParameters', instructionToLoad?.parameters);
        }

        if (onLoadInstruction) onLoadInstruction(instructionToLoad);
        setOpenModal(false);
    };

    const handleFilterModels = () => {
        if (!instructions || !selectedOrganization) {
            return [];
        }

        return instructions?.filter(instruction =>
            (instruction.unique_name?.toLowerCase().includes(searchTerm.toLowerCase())) &&
            (selectType === 'All' || instruction.model_type === selectType) //
        );
    };

    return (
        <Dialog open={openModal} onOpenChange={setOpenModal}>
            <DialogContent className="sm:max-w-[90vw] max-h-[90vh] min-h-[300px] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Select an Instruction</DialogTitle>
                    <DialogDescription>
                        Choose an instruction and view its details.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row items-center gap-4 p-4 bg-background border rounded-md sticky top-0 z-10">
                        <div className="flex flex-col w-full sm:flex-row gap-4 items-center">
                            <div className="flex-grow w-full">
                                <SearchBar onSearch={(value) => setSearchTerm(value)} />
                            </div>
                            <FilterBar
                                onSearch={(value) => setSelectType(value)}
                                selectModelType={selectType}
                                types={modelTypes.map((type, index) => ({
                                    id: index,
                                    name: type,
                                    value: type === 'Watson' ? 'IBM' : type
                                }))}
                            />
                        </div>
                        <Button className="flex gap-2 w-full sm:w-auto" type="button" onClick={reloadInstructions} disabled={isLoadingInstructions}>
                            {isLoadingInstructions ? 'Loading Instructions...' : 'Reload Instructions'}
                        </Button>
                    </div>
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex flex-col flex-1 min-w-0">
                            {handleFilterModels().length === 0 ? (
                                <div className="flex items-center justify-center w-full h-[28rem] text-gray-500">
                                    <span>No instructions saved.</span>
                                </div>
                            ) : (
                                <ScrollArea className="h-[28rem] flex-grow">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-4 p-4">
                                        {handleFilterModels().map((instruction) => (
                                            <Button
                                                key={instruction?._id || instruction.unique_name}
                                                variant={selectedInstruction === instruction.unique_name ? 'default' : 'outline'}
                                                onClick={() => handleInstructionSelect(instruction)}
                                                className={`justify-start h-auto text-left ${instruction?.isModelProviderExist === false ? 'bg-amber-100 hover:bg-amber-50 text-amber-700 border-amber-300' : ''}`}
                                            >
                                                <div className="w-full py-2">
                                                    <div className='flex w-full justify-between gap-2 items-center'>
                                                        <div className="font-semibold w-full truncate flex gap-1 items-center">
                                                            {instruction?.isModelProviderExist === false && (
                                                                <TooltipProvider>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <div className="flex items-center">
                                                                                <InfoIcon className="h-4 w-4 text-amber-500" />
                                                                            </div>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent className="max-w-xs">
                                                                            <p> The model '{instruction.modelId}' is deprecated </p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                </TooltipProvider>
                                                            )}

                                                            {instruction.unique_name}
                                                        </div>
                                                        <Badge className="px-1 rounded text-green-50">
                                                            {instruction?.model_type}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            </Button>
                                        ))}
                                    </div>
                                </ScrollArea>
                            )}
                        </div>
                        {instructionDetails && (
                            <DetailsCard
                                instructionDetails={instructionDetails}
                                handleInstructionLoad={handleInstructionLoad}
                                selectedInstruction={selectedInstruction}
                            />
                        )}
                    </div>
                </div>
                {!instructionDetails && (
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button type="button" variant="secondary">
                                Cancel
                            </Button>
                        </DialogClose>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
}